import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, BellOff, CheckCircle, AlertCircle, Loader2, Smartphone, Share, PlusSquare } from 'lucide-react';
import { usePushNotifications } from '@/lib/usePushNotifications';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    testNotification
  } = usePushNotifications();

  const [testing, setTesting] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Detect iOS and standalone mode on mount
  useEffect(() => {
    // Detect iOS - using function to avoid direct setState
    const detectIOSAndStandalone = () => {
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true;
      
      return { iOS, standalone };
    };
    
    const { iOS, standalone } = detectIOSAndStandalone();
    setIsIOS(iOS);
    setIsStandalone(standalone);
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      toast.success('Push-Benachrichtigungen aktiviert! Sie erhalten jetzt Terminerinnerungen.');
    }
  };

  const handleUnsubscribe = async () => {
    const success = await unsubscribe();
    if (success) {
      toast.success('Push-Benachrichtigungen deaktiviert');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    const success = await testNotification();
    setTesting(false);
    
    if (success) {
      toast.success('Test-Benachrichtigung gesendet!');
    } else {
      toast.error('Test fehlgeschlagen. Bitte überprüfen Sie die Einstellungen.');
    }
  };

  // iOS Safari not in standalone mode - show instructions
  if (isIOS && !isStandalone) {
    return (
      <Card className="border-blue-200 bg-blue-50" data-testid="notification-settings-ios">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Smartphone className="w-5 h-5" />
            Push-Benachrichtigungen auf iPhone/iPad
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-blue-700 text-sm">
            Um Push-Benachrichtigungen auf Ihrem iOS-Gerät zu erhalten, müssen Sie diese App zum Homescreen hinzufügen:
          </p>
          
          <div className="bg-white rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
              <div>
                <p className="font-medium text-sm">Tippen Sie auf das Teilen-Symbol</p>
                <div className="flex items-center gap-2 mt-1">
                  <Share className="w-5 h-5 text-blue-500" />
                  <span className="text-xs text-gray-500">(unten in Safari)</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
              <div>
                <p className="font-medium text-sm">Wählen Sie &quot;Zum Home-Bildschirm&quot;</p>
                <div className="flex items-center gap-2 mt-1">
                  <PlusSquare className="w-5 h-5 text-blue-500" />
                  <span className="text-xs text-gray-500">(scrollen Sie ggf. nach rechts)</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
              <div>
                <p className="font-medium text-sm">Öffnen Sie die App vom Homescreen</p>
                <span className="text-xs text-gray-500">Dann können Sie hier Benachrichtigungen aktivieren</span>
              </div>
            </div>
          </div>
          
          <div className="bg-amber-100 rounded-lg p-3">
            <p className="text-amber-800 text-xs">
              <strong>Hinweis:</strong> iOS unterstützt Web-Push-Benachrichtigungen nur für Apps, die zum Homescreen hinzugefügt wurden (seit iOS 16.4).
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Browser doesn't support push (non-iOS)
  if (!isSupported && !isIOS) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="w-5 h-5" />
            Browser nicht unterstützt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-amber-700 text-sm">
            Ihr Browser unterstützt keine Push-Benachrichtigungen. 
            Bitte verwenden Sie Chrome, Firefox, Edge oder Safari auf einem Desktop-Computer.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="notification-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#9B1B30]" />
              Push-Benachrichtigungen
            </CardTitle>
            <CardDescription className="mt-1">
              Erhalten Sie Erinnerungen auch wenn der Browser geschlossen ist
            </CardDescription>
          </div>
          <Badge variant={isSubscribed ? "default" : "secondary"} className={isSubscribed ? "bg-green-500" : ""}>
            {isSubscribed ? "Aktiv" : "Inaktiv"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Reminder Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-sm mb-2">Sie werden benachrichtigt:</h4>
          <ul className="space-y-1 text-sm text-gray-600">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              1 Tag vor dem Termin
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              45 Minuten vor dem Termin
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              20 Minuten vor dem Termin
            </li>
          </ul>
        </div>

        {/* Permission Status */}
        {permission === 'denied' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm">
              <strong>Hinweis:</strong> Benachrichtigungen wurden in den Browser-Einstellungen blockiert. 
              Bitte erlauben Sie Benachrichtigungen für diese Website in Ihren Browser-Einstellungen.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {isSubscribed ? (
            <>
              <Button
                variant="outline"
                onClick={handleUnsubscribe}
                disabled={loading}
                className="flex items-center gap-2"
                data-testid="unsubscribe-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellOff className="w-4 h-4" />}
                Deaktivieren
              </Button>
              <Button
                variant="secondary"
                onClick={handleTest}
                disabled={testing || loading}
                className="flex items-center gap-2"
                data-testid="test-notification-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Test senden
              </Button>
            </>
          ) : (
            <Button
              onClick={handleSubscribe}
              disabled={loading || permission === 'denied'}
              className="btn-primary flex items-center gap-2"
              data-testid="subscribe-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Benachrichtigungen aktivieren
            </Button>
          )}
        </div>

        {/* Browser Permission Hint */}
        {!isSubscribed && permission === 'default' && (
          <p className="text-xs text-gray-500">
            Nach dem Klicken auf &quot;Aktivieren&quot; fragt Ihr Browser nach der Erlaubnis für Benachrichtigungen.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
